import type { Component } from 'vue';
import {
    PhAt,
    PhBuildings,
    PhEnvelopeSimple,
    PhIdentificationCard,
    PhMailbox,
    PhMapPin,
    PhMapTrifold,
    PhKey,
    PhPhone,
    PhTextAa,
    PhUser,
} from '@phosphor-icons/vue';

/**
 * The field types the backend addresses a value by (app-api `Domains\Footprint\Enums\FieldType`).
 * A snapshot row, a records row and an add-to-vault handoff all speak this vocabulary.
 */
export type FootprintFieldType =
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'phone'
    | 'street_address'
    | 'city'
    | 'state'
    | 'zip'
    | 'username'
    | 'password'
    | 'ssn';

/** What a reader is told a value is: the word for it, and the glyph that stands in for the word. */
export interface FootprintFieldKind {
    label: string;
    icon: Component;
}

/**
 * The ONE field-kind vocabulary, keyed on the backend enum so a label and its glyph can never
 * disagree. A row shows the glyph and speaks the label; a panel and a drawer print the label.
 * Two tables — one of words, one of pictures — is how a value ends up called an address beside
 * an envelope.
 */
export const FOOTPRINT_FIELDS: Record<FootprintFieldType, FootprintFieldKind> = {
    first_name: { label: 'First name', icon: PhUser },
    last_name: { label: 'Last name', icon: PhUser },
    email: { label: 'Email', icon: PhEnvelopeSimple },
    phone: { label: 'Phone', icon: PhPhone },
    street_address: { label: 'Address', icon: PhMapPin },
    city: { label: 'City', icon: PhBuildings },
    state: { label: 'Region', icon: PhMapTrifold },
    zip: { label: 'ZIP', icon: PhMailbox },
    username: { label: 'Username', icon: PhAt },
    password: { label: 'Password', icon: PhKey },
    ssn: { label: 'SSN', icon: PhIdentificationCard },
};

/**
 * What to call a field type and what to draw for it. The backend owns the enum and may address
 * a value by a type this table has yet to learn, so an unknown key keeps its raw key as the
 * word and takes the generic text glyph: the row still renders, and a screen reader is still
 * told what kind of thing the value is.
 */
export function fieldKind(fieldType: string): FootprintFieldKind {
    // Own keys only: a raw payload string such as `constructor` would otherwise read a
    // prototype member and hand back a kind with no word and no glyph.
    return Object.hasOwn(FOOTPRINT_FIELDS, fieldType) ? FOOTPRINT_FIELDS[fieldType as FootprintFieldType] : { label: fieldType, icon: PhTextAa };
}
